import { useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ActiveClientProvider, useActiveClient } from "@/context/ActiveClientContext";
import {
  LayoutDashboard, Users, ClipboardList, Chrome,
  Settings as SettingsIcon, LogOut, Plane, Shield, UserCog, UsersRound, Briefcase,
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, roles: ["admin", "executive"] },
  { to: "/clients", label: "Clients", icon: Users, roles: ["admin", "executive"] },
  { to: "/executives", label: "Executives", icon: UsersRound, roles: ["admin"] },
  { to: "/tracker", label: "Application Tracker", icon: ClipboardList, roles: ["admin", "executive"] },
  { to: "/extension", label: "Extension Simulator", icon: Chrome, roles: ["admin", "executive"] },
  { to: "/settings", label: "Settings", icon: SettingsIcon, roles: ["admin", "executive"] },
];

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <div className="min-h-screen flex bg-[#090A0F]">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-[#0D0E15] border-r border-slate-800/80 flex flex-col fixed h-screen z-20">
        <div className="px-5 h-16 flex items-center gap-2.5 border-b border-slate-800/60">
          <div className="w-9 h-9 rounded-xl bg-indigo-500 flex items-center justify-center shadow-lg shadow-indigo-500/30">
            <Plane className="w-5 h-5 text-white" />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-extrabold text-slate-100 text-lg tracking-tight">ApplyPilot</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Job-Fit Engine</div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.filter((item) => !item.roles || item.roles.includes(user?.role)).map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150",
                  active
                    ? "bg-indigo-500/15 text-indigo-300 border border-indigo-500/30"
                    : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-100 border border-transparent"
                )}
              >
                <Icon className="w-[18px] h-[18px]" />
                <span className="flex-1">{item.label}</span>
                {item.badge && (
                  <span className="text-[9px] uppercase font-bold tracking-wide bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t border-slate-800/60">
          <div className="flex items-center gap-3 px-2 py-2 rounded-lg">
            <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center text-slate-300 font-semibold text-sm">
              {user?.name?.[0] || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-200 truncate">{user?.name}</div>
              <div className="flex items-center gap-1 text-[11px] text-slate-500">
                {user?.role === "admin" ? <Shield className="w-3 h-3" /> : <UserCog className="w-3 h-3" />}
                {user?.role === "admin" ? "Team Lead / Admin" : "Executive"}
              </div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={() => { logout(); navigate("/login"); }}
            className="mt-1 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:bg-rose-500/10 hover:text-rose-400 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-64 min-w-0">
        <ActiveClientProvider>
          <TopBar />
          <main className="max-w-[1600px] mx-auto px-6 py-8">{children}</main>
        </ActiveClientProvider>
      </div>
    </div>
  );
}

function TopBar() {
  const { clients, activeClientId, setActiveClientId, activeClient } = useActiveClient();
  return (
    <header className="sticky top-0 z-10 h-14 border-b border-slate-800/70 bg-[#0D0E15]/90 backdrop-blur-md">
      <div className="max-w-[1600px] mx-auto px-6 h-full flex items-center gap-3">
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Briefcase className="w-4 h-4 text-indigo-400" />
          <span className="hidden sm:inline">Working for</span>
        </div>
        <div className="relative">
          <select
            data-testid="active-client-select"
            value={activeClientId}
            onChange={(e) => setActiveClientId(e.target.value)}
            className="appearance-none bg-[#12151F] border border-slate-800 rounded-lg pl-3 pr-9 py-1.5 text-sm font-semibold text-slate-100 outline-none focus:border-indigo-500/60 transition-colors cursor-pointer min-w-[180px]"
          >
            {clients.length === 0 && <option value="">No clients yet</option>}
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <UserCog className="w-4 h-4 text-slate-500 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        {activeClient && (
          <span className={cn(
            "text-[11px] px-2 py-0.5 rounded-full border",
            activeClient.profile
              ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"
              : "text-amber-400 border-amber-500/30 bg-amber-500/10"
          )}>
            {activeClient.profile ? "Profile ready" : "No profile"}
          </span>
        )}
        {activeClient && (
          <Link
            to={`/tracker?client=${activeClient.id}`}
            data-testid="topbar-view-tracker"
            className="ml-auto text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
          >
            View this client's tracker →
          </Link>
        )}
      </div>
    </header>
  );
}
